import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../store/store";
import { setDraftParticipants, setSelectedConversation } from "../../store/slices/conversation.slice";
import { setSelectedDid } from "../../store/slices/SmsDids.slice";
import { useGetSmsDids } from "../apis/SmsDids.api";
import { useSmsProtocol } from "./useSmsProtocol";
import { parseSmsProtocolPhone } from "../../utils/smsProtocol";
import { isElectronShell } from "../../utils/electron";
import { searchConversationsApi } from "../apis/conversations.api";
import { phoneNumberFormatE164 } from "../../utils/phoneLib";

export function useSmsProtocolDidPicker() {
  const inElectron = isElectronShell();
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: smsDids } = useSelector((state: RootState) => state.smsDids);
  const { isFetching: isLoading } = useGetSmsDids();

  const [pendingPhone, setPendingPhone] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const openDidPicker = useCallback((phone: string) => {
    console.log("[sms-protocol] opening DID picker for:", phone);
    setPendingPhone(phone);
    setModalOpen(true);
  }, []);

  const handleSmsProtocol = useCallback(
    (url: string) => {
      const phone = parseSmsProtocolPhone(url);
      if (!phone) {
        console.log("[sms-protocol] could not parse phone from url:", url);
        return;
      }
      openDidPicker(phone);
    },
    [openDidPicker]
  );

  useSmsProtocol(handleSmsProtocol, inElectron);

  const handleSelect = useCallback(
    async (did: string) => {
      if (!pendingPhone) return;
      dispatch(setSelectedDid(did));
      const normalizedPhone = phoneNumberFormatE164(pendingPhone);
      if (!normalizedPhone) {
        setModalOpen(false);
        setPendingPhone(null);
        return;
      }
      const conversations = await searchConversationsApi({
        did,
        search: normalizedPhone,
        participants: [normalizedPhone],
        searchType: "contact",
        before: new Date().toISOString(),
      });
      const conversation = conversations.find(
        (item) =>
          item.type === "direct" &&
          item.participants.length === 1 &&
          phoneNumberFormatE164(item.participants[0]) === normalizedPhone
      );
      if (conversation) {
        dispatch(setSelectedConversation({
          did,
          conversation_id: conversation.conversation_id,
          participants: conversation.participants,
          type: conversation.type,
        }));
      } else {
        dispatch(setSelectedConversation(null));
        dispatch(setDraftParticipants([normalizedPhone]));
      }
      setModalOpen(false);
      setPendingPhone(null);
      if (location.pathname !== "/") {
        navigate("/");
      }
    },
    [pendingPhone, dispatch, location.pathname, navigate]
  );

  const handleClose = useCallback(() => {
    setModalOpen(false);
    setPendingPhone(null);
  }, []);

  if (!inElectron) {
    return {
      modalOpen: false,
      pendingPhone: null,
      smsDids: [],
      isLoading: false,
      handleSelect: () => {},
      handleClose: () => {},
    };
  }

  return {
    modalOpen,
    pendingPhone,
    smsDids,
    isLoading,
    handleSelect,
    handleClose,
  };
}
